/* eslint-disable no-template-curly-in-string */
const HTML_VALIDATION_PATTERNS = [
  {
    text: 'US ZIP CODE',
    value: '^[0-9]{5}([ \\-]?[0-9]{4})?$',
    message: 'Invalid ZIP Code: must be either 5 digits or 9 digits',
  },
  {
    text: 'CANADA POSTAL CODE',
    value: '^[A-Za-z]d[A-Za-z][ ]?[0-9][A-Za-z][0-9]$',
    message: 'Invalid Postal Code: must include a letter, a digit, and a letter in the format A1A 1A1',
  },
];

(function () {
  if (!Element.prototype.checkVisibility) {
    Object.defineProperty(Element.prototype, 'checkVisibility', {
      value({ checkOpacity = false, checkVisibilityCSS = false } = {}) {
        if (!this.isConnected) return false;
        const styles = getComputedStyle(this);
        if (styles.getPropertyValue('display') === 'contents') return false;
        if (checkVisibilityCSS && styles.getPropertyValue('visibility') !== 'visible') return false;
        let node = this;
        while (node) {
          const nodeStyles = node === this ? styles : getComputedStyle(node);
          if (nodeStyles.getPropertyValue('display') === 'none') return false;
          if (checkOpacity && nodeStyles.getPropertyValue('opacity') === '0') return false;
          if (node !== this && nodeStyles.getPropertyValue('content-visibility') === 'hidden') {
            return false;
          }
          if (!node.parentElement && node.getRootNode() instanceof ShadowRoot) {
            node = node.getRootNode().host;
          } else {
            node = node.parentElement;
          }
        }
        return true;
      },
    });
  }
})();

const FormValidationService = () => {
  const DEFAULT_ERROR_MESSAGES = {
    valueMissing: 'Field is required',
    typeMismatch: 'Please enter a valid email address',
    tooShort:
      'Please lengthen this text to ${minLength} characters or more (you are currently using ${length} characters).',
    tooLong:
      'Please shorten this text to no more than ${maxLength} characters (you are currently using ${length} characters).',
    patternMismatch: 'Please match the requested format.',
    badInput: 'Please enter a number.',
    stepMismatch: 'Please select a valid value.',
    rangeOverflow: 'Please select a value that is no more than ${max}.',
    rangeUnderflow: 'Please select a value that is no less than ${min}.',
  };
  const ERROR_MODE_OPTIONS = {
    ERROR_TEXT: 'errorText',
    CUSTOM_VALIDITY: 'customValidity',
  };
  const VALIDATE_WHEN_OPTIONS = {
    BLUR: 'blur',
    CHANGE: 'change',
    SUBMIT: 'submit',
    NONE: '',
  };
  const VALIDATE_IF_OPTIONS = {
    TOUCHED: 'touched',
    DIRTY: 'dirty',
    ALWAYS: 'always',
  };
  const DEFAULT_ERRORS_TO_VALIDATE = [
    'valueMissing',
    'typeMismatch',
    'tooShort',
    'tooLong',
    'patternMismatch',
    'badInput',
    'stepMismatch',
    'rangeOverflow',
    'rangeUnderflow',
  ];
  const defaultValidationOptions = {
    prependLabelToError: false,
    errorsToValidate: DEFAULT_ERRORS_TO_VALIDATE,
    setFocusOnError: true,
    validateWhen: VALIDATE_WHEN_OPTIONS.BLUR,
    validateIf: VALIDATE_IF_OPTIONS.ALWAYS,
    errorMode: ERROR_MODE_OPTIONS.ERROR_TEXT,
    idAccessor: 'properties.id',
    errorTextPrefix: 'error-text-',
    errorTextSuffix: '',
    labelPrefix: 'label-',
    labelSuffix: '',
    messageAccessor: 'properties',
  };
  const forms = {};

  const get = (name) => forms[name] || forms;
  const add = (name, formID, fields, options) => {
    const form = create(name, formID, fields, options);
    forms[name] = form;
    return form;
  };

  const getProperty = (obj, path, defaultValue) => {
    const keys = path.split('.');
    let value = obj;
    for (let key of keys) {
      if (!value || typeof value !== 'object') return defaultValue;
      value = value[key];
    }
    return value !== undefined ? value : defaultValue;
  };

  const debounce = (func, delay) => {
    let inDebounce;
    return function (event, formName) {
      const context = this;
      const currentTarget = event.currentTarget;
      const target = event.target;
      const evt = { ...event, currentTarget, target };
      clearTimeout(inDebounce);
      inDebounce = setTimeout(() => func.apply(context, [evt, formName]), delay);
    };
  };

  const create = (formName, formID, fields, options) => {
    const getErrorMessages = (validationOptions) => ({
      ...DEFAULT_ERROR_MESSAGES,
      ...(validationOptions.messages || {}),
    });

    const createValidationOptions = (validationOptions) => ({
      ...defaultValidationOptions,
      ...validationOptions,
    });

    const getFormValues = (form) => Object.fromEntries(new FormData(form));

    const generateFieldState = (fields, idAccessor, initialValues) =>
      fields.reduce((acc, field) => {
        const id = getProperty(field, idAccessor, false);
        return {
          ...acc,
          [id]: {
            data: field,
            dirty: false,
            touched: false,
            initialValue: initialValues[id] || '',
            currentValue: initialValues[id] || '',
          },
        };
      }, {});

    const getFieldState = () => fieldState;

    const getValidationOptions = () => validationOptions;

    const setDirtyState = (id, value) => {
      if (fieldState[id]) fieldState[id].dirty = fieldState[id].initialValue !== value;
    };

    const setTouchedState = (id) => {
      if (fieldState[id]) fieldState[id].touched = true;
    };

    const setCurrentValue = (id, value) => {
      if (fieldState[id]) fieldState[id].currentValue = value;
    };

    const checkValidateIf = (validationObj, options) => {
      if (options.validateIf === VALIDATE_IF_OPTIONS.ALWAYS) return true;
      // required should always be validated
      if (options.errorsToValidate.includes('valueMissing') && validationObj.validity.valueMissing === true)
        return true;
      if (options.validateIf === VALIDATE_IF_OPTIONS.TOUCHED) return validationObj.fieldState.touched;
      if (options.validateIf === VALIDATE_IF_OPTIONS.DIRTY) return validationObj.fieldState.dirty;
    };

    const checkIfErrorTypeShouldBeValidated = (validationObj, options) =>
      options.errorsToValidate.some((key) => validationObj.validity[key] === true);

    const filterFieldsToValidate = (validationObj, options) =>
      checkIfFieldIsVisible(validationObj.element) &&
      checkValidateIf(validationObj, options) &&
      !validationObj.validity.valid &&
      checkIfErrorTypeShouldBeValidated(validationObj, options);

    const createErrorTextSelector = (id, { errorTextPrefix, errorTextSuffix }) =>
      `${errorTextPrefix}${id}${errorTextSuffix}`;

    const getErrorTextElement = (id, { errorTextPrefix, errorTextSuffix }) =>
      document.getElementById(`${errorTextPrefix}${id}${errorTextSuffix}`);

    const getLabelElement = (id, { labelPrefix, labelSuffix }) =>
      document.getElementById(`${labelPrefix}${id}${labelSuffix}`) || document.querySelector(`label[for="${id}"]`);

    const checkIfFieldIsVisible = (el) => {
      if (el) return el.checkVisibility();
      return false;
    };

    const getFieldValidationObj = (id, field, messageAccessor, prependLabelToError, rest) => {
      const allValidationObjs = {
        ...(validationState?.valid || {}),
        ...(validationState?.invalid || {}),
      };
      const existingValidationObj = allValidationObjs[id];
      if (existingValidationObj) {
        return {
          ...existingValidationObj,
          validity: existingValidationObj.validity,
          message: getErrorMessage(
            existingValidationObj.element,
            field,
            messageAccessor,
            prependLabelToError,
            errorMessages,
            existingValidationObj.validity,
            existingValidationObj.elementLabel
          ),
        };
      }
      const el = document.getElementById(id);
      if (el && ['input', 'select', 'textarea'].includes(el.tagName.toLowerCase())) {
        const elErrorText = getErrorTextElement(id, rest);
        const elLabel = getLabelElement(id, rest);
        return {
          id,
          element: el,
          elementLabel: elLabel,
          elementErrorText: elErrorText,
          fieldState: fieldState[id],
          validity: el.validity,
          message: getErrorMessage(
            el,
            field,
            messageAccessor,
            prependLabelToError,
            errorMessages,
            el.validity,
            elLabel
          ),
        };
      }
      return false;
    };

    const getValidationState = ({ idAccessor, messageAccessor, prependLabelToError, ...rest }, fields) => {
      validationState = fields.reduce(
        (acc, field) => {
          const id = getProperty(field, idAccessor, false);
          const validationObj = getFieldValidationObj(id, field, messageAccessor, prependLabelToError, rest);
          if (!validationObj) return acc;
          const shouldCheck = filterFieldsToValidate(validationObj, rest);
          validationObj?.element?.setCustomValidity?.('');
          if (shouldCheck) {
            return {
              ...acc,
              invalid: {
                ...acc.invalid,
                [id]: validationObj,
              },
            };
          }
          return {
            ...acc,
            valid: {
              ...acc.valid,
              [id]: validationObj,
            },
          };
        },
        { valid: {}, invalid: {} }
      );
      return { ...validationState, isValid: Object.values(validationState.invalid).length === 0 };
    };

    const populateTemplate = (template, values) =>
      typeof template === 'string' && template.replace(/\${(.*?)}/g, (match, key) => values[key.trim()]);

    const getInvalidStateKey = (validityState) => {
      for (let key in validityState) {
        if (validityState[key] && key !== 'valid') return key;
      }
      return undefined;
    };

    const getErrorMessage = (el, field, messageAccessor, prependLabelToError, messages, validityState, labelEl) => {
      let message = '';
      const errorKey = getInvalidStateKey(validityState);
      message =
        getProperty(field, `${messageAccessor}${messageAccessor ? '.' : ''}${errorKey}Message`, false) ||
        messages[errorKey];

      if (prependLabelToError && message) {
        message = `${labelEl?.innerText || ''} ${message}`;
      }
      return (
        populateTemplate(message, {
          minLength: el.minLength,
          length: el.value.length,
          maxLength: el.maxLength,
          max: el.max,
          min: el.min,
          value: el.value,
          label: labelEl?.innerText || '',
        }) || ''
      );
    };

    const reportValidity = (validationState, useErrorText, customErrorHandler) => {
      if (customErrorHandler) {
        return customErrorHandler(validationState);
      }
      if (useErrorText) {
        clearErrorText(validationState.valid);
        return Object.values(validationState.invalid).forEach((state) => {
          setErrorText(state);
        });
      }
      if (Object.values(validationState.invalid).length) {
        const invalidField = Object.values(validationState.invalid)[0];
        invalidField.element.setCustomValidity(invalidField.message);
        invalidField.element.reportValidity();
      }
    };

    const getCurrentValidationState = (fieldsToTest = fields) => getValidationState(validationOptions, fieldsToTest);

    const testValidity = (fieldsToTest = fields) => {
      const validationState = getValidationState(validationOptions, fieldsToTest);
      reportValidity(
        validationState,
        validationOptions.errorMode === ERROR_MODE_OPTIONS.ERROR_TEXT,
        validationOptions?.onError
      );
      validationOptions?.onTest?.(validationState);
      return validationState;
    };

    // ERROR TEXT
    const createErrorTextElement = (state) => {
      const errorTextElement = document.createElement('span');
      errorTextElement.style.fontSize = '0.8em';
      errorTextElement.style.color = '#B94A48';
      errorTextElement.id = createErrorTextSelector(state.id, validationOptions);
      errorTextElement.textContent = state.message;
      state.errorTextElement = errorTextElement;
      state.element.after(errorTextElement);
    };

    const setErrorText = (state) => {
      if (!state.errorTextElement) createErrorTextElement(state);
      state.errorTextElement.textContent = state.message;
      state.errorTextElement.classList.add('error-text-validation-error');
      state.element.classList.add('input-validation-error');
      state.element.style.backgroundColor = '#F2DEDE';
    };

    const clearErrorText = (validFields) => {
      Object.values(validFields).forEach((state) => {
        if (state.errorTextElement) {
          state.errorTextElement.textContent = '';
          state.errorTextElement.style.backgroundColor = '';
          state.errorTextElement.classList.remove('error-text-validation-error');
        }
        state.element.style.backgroundColor = '';
        state.element.classList.remove('input-validation-error');
      });
    };

    const onInput = (event, formName) => {
      const target = event.target;
      const form = get(formName);
      const fieldState = form.getFieldState();
      const validationOptions = form.getValidationOptions();
      if (target && ['input', 'select', 'textarea'].includes(target.tagName.toLowerCase())) {
        form.setCurrentValue(target.id, target.value);
        form.setDirtyState(target.id, target.value);
      }
      if (fieldState[target?.id] && validationOptions.validateWhen === VALIDATE_WHEN_OPTIONS.CHANGE) {
        form.testValidity([fieldState[target.id].data]);
      }
    };

    const debounceOnInput = debounce(onInput, 200);

    const onBlur = (event, formName) => {
      const target = event.target;
      const form = get(formName);
      const fieldState = form.getFieldState();
      const validationOptions = form.getValidationOptions();
      if (fieldState[target?.id] && validationOptions.validateWhen === VALIDATE_WHEN_OPTIONS.BLUR) {
        form.testValidity([fieldState[target.id].data]);
      }
    };

    const onFocus = (event, formName) => {
      const target = event.target;
      if (target && ['input', 'select', 'textarea'].includes(target.tagName.toLowerCase())) {
        const form = get(formName);
        form.setTouchedState(target.id);
      }
    };

    const addListeners = (formElement, formName) => {
      formElement.addEventListener('input', (event) => debounceOnInput(event, formName), true);
      formElement.addEventListener('blur', (event) => onBlur(event, formName), true);
      formElement.addEventListener('focus', (event) => onFocus(event, formName), true);
    };

    let validationState = {};
    const validationOptions = createValidationOptions(options);
    const formElement = document.getElementById(formID);
    const initialValues = getFormValues(formElement);
    const fieldState = generateFieldState(fields, validationOptions.idAccessor, initialValues);
    const errorMessages = getErrorMessages(validationOptions);
    addListeners(formElement, formName);

    return {
      getCurrentValidationState,
      testValidity,
      reportValidity,
      setDirtyState,
      setTouchedState,
      setCurrentValue,
      getFieldState,
      getValidationOptions,
    };
  };
  return {
    DEFAULT_ERRORS_TO_VALIDATE,
    DEFAULT_ERROR_MESSAGES,
    ERROR_MODE_OPTIONS,
    VALIDATE_WHEN_OPTIONS,
    VALIDATE_IF_OPTIONS,
    HTML_VALIDATION_PATTERNS,
    get,
    add,
    create,
  };
};

if (typeof module !== 'undefined' && module.exports) module.exports = FormValidationService;
else globalThis.formValidationService = FormValidationService();
